/**
 * Office Configuration
 * -------------------------------------------------------------------
 * Each office location gets its own config file. To add a new office,
 * create a new config object following this structure and import it
 * based on the URL path or subdomain (e.g., /wauwatosa, /tampa).
 *
 * The floor plan SVG coordinates define where each room appears on
 * the interactive map. The viewBox of the SVG is set by svgViewBox.
 *
 * Room email addresses correspond to M365 resource mailboxes.
 * -------------------------------------------------------------------
 */

export const OFFICE_CONFIGS = {
  wauwatosa: {
    id: 'wauwatosa',
    name: 'Wisconsin',
    fullName: 'SSP Innovations - Wisconsin Office',
    documentTitle: 'SSP Innovations - Room Booking',
    address: 'Wauwatosa, WI',
    // Postal address used for the calendar event location on bookings
    postalAddress: {
      street: '10100 W. Innovation Drive',
      city: 'Wauwatosa',
      state: 'WI',
      postalCode: '53226',
      countryOrRegion: 'US',
    },
    svgViewBox: '110 100 1098 570',
    floorplanImage: '/MKE_FloorPlan.svg', // Architectural SVG used as background
    floorplanSize: { w: 1224, h: 792 },   // Full image dimensions (viewBox crops to visible area)
    mapTitle: 'MKE - Floor Plan',

    // Hidden easter eggs: the IT Room, and the gap between the kitchen
    // island and counters (left of C5/C9, below the right side of O3)
    easterEggs: [
      {
        x: 928, y: 182, w: 80, h: 140,
        text: 'HAVE YOU TRIED\nTURNING IT OFF\nAND ON AGAIN?',
      },
      {
        x: 600, y: 423, w: 64, h: 144,
        text: 'OPE! SORRY,\nJUST GONNA SNEAK\nRIGHT PAST YA',
      },
    ],

    // Per-office branding. Values match the original SSP styling exactly,
    // so this theme is a no-op for MKE (CSS falls back to the same values).
    theme: {
      headerBg: '#47ACE4',
      headerAccent: 'transparent',
      headingFont: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      accent: '#47ACE4',          // kiosk calendar headings
      sectionHeading: '#193d68',  // light-section headings
      accent2: '#2a8bc4',         // office/cubicle headings
      buttonBg: '#193d68',        // guest wifi button
      logo: '/logo-full.png',
      logoAlt: 'SSP Innovations',
      wifi: { network: 'SSPGUEST', password: 'sspismovingonup' },
      map: {
        shared: { available: '#193d68', booked: '#5D5E60', hover: '#2a5a94', text: '#fff', bookedText: '#fff' },
        desk:   { available: '#47ACE4', booked: '#5D5E60', hover: '#6fc0ec', text: '#fff', bookedText: '#fff' },
        border: null,        // null = outline matches fill (original behavior)
        borderWidth: 2.5,
        unbookableFill: '#fff',
        unbookableText: '#333',
        unbookableSub: '#555',
        legend: [
          ['#193d68', 'Conference / Meeting / Huddle'],
          ['#47ACE4', 'Office / Cubicle'],
          ['#5D5E60', 'Booked'],
        ],
      },
    },

    // Room definitions - coordinates mapped to MKE_FloorPlan.svg
    rooms: [
      // Conference Room
      {
        id: 'lombardi',
        name: 'Lombardi Conference Room',
        type: 'conference',
        capacity: 20,
        email: 'mke.cr.lombardi@sspinnovations.com',
        floor: { x: 140, y: 182, w: 187, h: 140 },
      },
      // Meeting Room
      {
        id: 'starr',
        name: 'Starr Meeting Room',
        type: 'meeting',
        capacity: 10,
        email: 'mke.mr.starr@sspinnovations.com',
        floor: { x: 1065, y: 478, w: 113, h: 84 },
      },
      // Huddle Rooms
      {
        id: 'lambeau',
        name: 'Lambeau Huddle Room',
        type: 'huddle',
        capacity: 5,
        email: 'mke.hr.lambeau@sspinnovations.com',
        floor: { x: 210, y: 410, w: 98, h: 120 },
      },
      {
        id: 'holmgren',
        name: 'Holmgren Huddle Room',
        type: 'huddle',
        capacity: 5,
        email: 'mke.hr.holmgren@sspinnovations.com',
        floor: { x: 312, y: 410, w: 100, h: 120 },
      },
      // Offices O1-O6 (top row, sized to end at wall above C4)
      {
        id: 'O1',
        name: 'Office O1',
        type: 'office',
        capacity: 1,
        email: 'mke.o1@sspinnovations.com',
        floor: { x: 332, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O2',
        name: 'Office O2',
        type: 'office',
        capacity: 1,
        email: 'mke.o2@sspinnovations.com',
        floor: { x: 425, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O3',
        name: 'Office O3',
        type: 'office',
        capacity: 1,
        email: 'mke.o3@sspinnovations.com',
        floor: { x: 518, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O4',
        name: 'Office O4',
        type: 'office',
        capacity: 1,
        email: 'mke.o4@sspinnovations.com',
        floor: { x: 611, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O5',
        name: 'Office O5',
        type: 'office',
        capacity: 1,
        email: 'mke.o5@sspinnovations.com',
        floor: { x: 704, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O6',
        name: 'Office O6',
        type: 'office',
        capacity: 1,
        email: 'mke.o6@sspinnovations.com',
        floor: { x: 797, y: 182, w: 89, h: 140 },
      },
      // O7 - Hannah Medaglia (non-bookable)
      {
        id: 'O7',
        name: 'Office O7',
        type: 'office',
        capacity: 1,
        bookable: false,
        assignedTo: 'H. Medaglia',
        mapLabel: 'Data Manager',
        floor: { x: 1065, y: 564, w: 113, h: 88 },
      },
      // Non-bookable rooms (shown on map only)
      {
        id: 'it-room',
        name: 'IT Room',
        type: 'utility',
        bookable: false,
        floor: { x: 928, y: 182, w: 80, h: 140 },
      },
      {
        id: 'mothers-room',
        name: "Mother's Room",
        type: 'utility',
        bookable: false,
        floor: { x: 1065, y: 395, w: 113, h: 80 },
      },
      // Cubicles C1-C8 (4 columns x 2 rows)
      { id: 'C1',  name: 'Cubicle C1',  type: 'cubicle', capacity: 1, email: 'mke.c1@sspinnovations.com',  floor: { x: 666, y: 355, w: 55, h: 62 } },
      { id: 'C2',  name: 'Cubicle C2',  type: 'cubicle', capacity: 1, email: 'mke.c2@sspinnovations.com',  floor: { x: 725, y: 355, w: 55, h: 62 } },
      { id: 'C3',  name: 'Cubicle C3',  type: 'cubicle', capacity: 1, email: 'mke.c3@sspinnovations.com',  floor: { x: 784, y: 355, w: 55, h: 62 } },
      { id: 'C4',  name: 'Cubicle C4',  type: 'cubicle', capacity: 1, email: 'mke.c4@sspinnovations.com',  floor: { x: 843, y: 355, w: 55, h: 62 } },
      { id: 'C5',  name: 'Cubicle C5',  type: 'cubicle', capacity: 1, email: 'mke.c5@sspinnovations.com',  floor: { x: 666, y: 423, w: 55, h: 62 } },
      { id: 'C6',  name: 'Cubicle C6',  type: 'cubicle', capacity: 1, email: 'mke.c6@sspinnovations.com',  floor: { x: 725, y: 423, w: 55, h: 62 } },
      { id: 'C7',  name: 'Cubicle C7',  type: 'cubicle', capacity: 1, email: 'mke.c7@sspinnovations.com',  floor: { x: 784, y: 423, w: 55, h: 62 } },
      { id: 'C8',  name: 'Cubicle C8',  type: 'cubicle', capacity: 1, email: 'mke.c8@sspinnovations.com',  floor: { x: 843, y: 423, w: 55, h: 62 } },
      // Cubicles C9-C20 (6 columns x 2 rows, aligned under C1-C8)
      { id: 'C9',  name: 'Cubicle C9',  type: 'cubicle', capacity: 1, email: 'mke.c9@sspinnovations.com',  floor: { x: 666, y: 505, w: 55, h: 62 } },
      { id: 'C10', name: 'Cubicle C10', type: 'cubicle', capacity: 1, email: 'mke.c10@sspinnovations.com', floor: { x: 725, y: 505, w: 55, h: 62 } },
      { id: 'C11', name: 'Cubicle C11', type: 'cubicle', capacity: 1, email: 'mke.c11@sspinnovations.com', floor: { x: 784, y: 505, w: 55, h: 62 } },
      { id: 'C12', name: 'Cubicle C12', type: 'cubicle', capacity: 1, email: 'mke.c12@sspinnovations.com', floor: { x: 843, y: 505, w: 55, h: 62 } },
      { id: 'C13', name: 'Cubicle C13', type: 'cubicle', capacity: 1, email: 'mke.c13@sspinnovations.com', floor: { x: 902, y: 505, w: 55, h: 62 } },
      { id: 'C14', name: 'Cubicle C14', type: 'cubicle', capacity: 1, email: 'mke.c14@sspinnovations.com', floor: { x: 961, y: 505, w: 55, h: 62 } },
      { id: 'C15', name: 'Cubicle C15', type: 'cubicle', capacity: 1, email: 'mke.c15@sspinnovations.com', floor: { x: 666, y: 573, w: 55, h: 62 } },
      { id: 'C16', name: 'Cubicle C16', type: 'cubicle', capacity: 1, email: 'mke.c16@sspinnovations.com', floor: { x: 725, y: 573, w: 55, h: 62 } },
      { id: 'C17', name: 'Cubicle C17', type: 'cubicle', capacity: 1, email: 'mke.c17@sspinnovations.com', floor: { x: 784, y: 573, w: 55, h: 62 } },
      { id: 'C18', name: 'Cubicle C18', type: 'cubicle', capacity: 1, email: 'mke.c18@sspinnovations.com', floor: { x: 843, y: 573, w: 55, h: 62 } },
      { id: 'C19', name: 'Cubicle C19', type: 'cubicle', capacity: 1, email: 'mke.c19@sspinnovations.com', floor: { x: 902, y: 573, w: 55, h: 62 } },
      { id: 'C20', name: 'Cubicle C20', type: 'cubicle', capacity: 1, email: 'mke.c20@sspinnovations.com', floor: { x: 961, y: 573, w: 55, h: 62 } },
    ],

    // Non-bookable areas (for visual context when no floorplan image)
    fixedAreas: [
      { label: 'Storage',           x: 78,   y: 328,  w: 80,  h: 40 },
      { label: 'Reception',         x: 133,  y: 370,  w: 85,  h: 30 },
      { label: 'Cafe',              x: 405,  y: 550,  w: 80,  h: 85 },
      { label: 'Back Entrance',     x: 680,  y: 290,  w: 120, h: 18 },
    ],
  },

  hsv: {
    id: 'hsv',
    name: 'Huntsville',
    fullName: '3-GIS - Huntsville Office',
    documentTitle: '3-GIS - Room Booking',
    address: 'Huntsville, AL',
    // Postal address used for the calendar event location on bookings
    postalAddress: {
      street: '115 Clinton Ave E, Suite 3C',
      city: 'Huntsville',
      state: 'AL',
      postalCode: '35801',
      countryOrRegion: 'US',
    },
    svgViewBox: '158 0 2002 815', // Cropped: Pathfinder L wall at left edge, building right wall visible
    floorplanImage: '/HSV_FloorPlan.png', // Rendered from 3-GIS Schematic Plan (7/24/2024)
    floorplanSize: { w: 2165, h: 815 },   // Full image dimensions (PNG is 2x this for crispness)
    mapTitle: 'HSV - Floor Plan',
    // Floor-plan label font scale. HSV's viewBox (~2002 wide) is ~1.8x MKE's,
    // so labels need scaling up to stay legible. Tune if still too small/large.
    labelScale: 1.8,

    // Hidden easter eggs: tap the empty open area between D5 and Michael's
    // office, or the IT Room / HVAC Chase
    easterEggs: [
      {
        x: 684, y: 269, w: 225, h: 170,
        text: 'THERES SO\nMUCH MORE\nROOM FOR\nACTIVITIES!',
      },
      {
        x: 1480, y: 21, w: 28, h: 177,
        text: 'DID WE JUST BECOME BEST FRIENDS?',
      },
    ],

    // 3-GIS branding (Brand Identity Manual): Pantone 361 green (#50B948 from
    // logo artwork), 30% tint, black 100%/60%. Headings: Lubalin Graph.
    theme: {
      headerBg: '#2E3338',
      headerDark: '#2E3338', // web/mobile header background
      headerAccent: '#50B948',
      subtitleTag: 'Room Booking',
      headingFont: "'Lubalin Graph', 'Rockwell', serif",
      accent: '#50B948',
      sectionHeading: '#231F20',
      accent2: '#231F20',
      buttonBg: '#50B948',
      logo: '/logo-3gis-white.png',
      logoIcon: '/logo-3gis-white.png',   // mobile header (dark background)
      logoLight: '/logo-3gis-primary.png', // mobile login gate (light background)
      logoAlt: '3-GIS by SSP Innovations',
      wifi: { network: '3-GIS-GUEST', password: '3gisismovingonup' },
      map: {
        shared: { available: '#CCE5C0', booked: '#A7A9AC', hover: '#b9dcaa', text: '#231F20', bookedText: '#231F20' },
        desk:   { available: '#50B948', booked: '#A7A9AC', hover: '#6fc763', text: '#fff', bookedText: '#231F20' },
        border: '#231F20',   // thin black outlines on all rooms
        borderWidth: 1,
        unbookableFill: '#231F20',
        unbookableText: '#fff',
        unbookableSub: '#c9c9c9',
        legend: [
          ['#CCE5C0', 'Conf / Mtg / Huddle'],
          ['#50B948', 'Desk'],
          ['#A7A9AC', 'Booked'],
          ['#231F20', 'Not bookable'],
        ],
      },
    },

    // Room definitions - coordinates mapped to HSV_FloorPlan.png
    // Name-to-room mapping confirmed by Casey 2026-06-15: left to right the rooms
    // are Pathfinder, Atlantis, Discovery, Endeavour. The floor.x values below
    // already reflect that order (Pathfinder 158, Atlantis 304, Discovery 605,
    // Endeavour 906).
    rooms: [
      // Conference / meeting rooms (existing Exchange mailboxes, renamed to @3-gis.com)
      {
        id: 'pathfinder',
        name: 'Pathfinder Meeting Room',
        type: 'meeting',
        capacity: 6,
        email: 'hsv.mr.pathfinder@3-gis.com',
        floor: { x: 158, y: 21, w: 132, h: 177 },
      },
      {
        id: 'discovery',
        name: 'Discovery Conference Room',
        type: 'conference',
        capacity: 6,
        email: 'hsv.cr.discovery@3-gis.com',
        floor: { x: 605, y: 21, w: 287, h: 177 },
      },
      {
        id: 'atlantis',
        name: 'Atlantis Conference Room',
        type: 'conference',
        capacity: 12,
        email: 'hsv.cr.atlantis@3-gis.com',
        floor: { x: 304, y: 21, w: 287, h: 177 },
      },
      {
        id: 'endeavour',
        name: 'Endeavour Meeting Room',
        type: 'meeting',
        capacity: 6,
        email: 'hsv.mr.endeavour@3-gis.com',
        floor: { x: 906, y: 21, w: 132, h: 177 },
      },
      // Focus Rooms (phone booths, new mailboxes)
      {
        id: 'focus-a',
        name: 'Focus Room A',
        type: 'huddle',
        capacity: 2,
        email: 'hsv.hr.focusa@3-gis.com',
        floor: { x: 1427, y: 328, w: 85, h: 52 },
      },
      {
        id: 'focus-b',
        name: 'Focus Room B',
        type: 'huddle',
        capacity: 2,
        email: 'hsv.hr.focusb@3-gis.com',
        floor: { x: 1427, y: 269, w: 85, h: 52 },
      },
      {
        id: 'mothers-room',
        name: "Mother's Room",
        type: 'utility',
        bookable: false,
        floor: { x: 1427, y: 387, w: 85, h: 52 },
      },
      // Common areas (map only)
      { id: 'lounge-left',  name: 'Lounge', type: 'utility', bookable: false, commonArea: true, floor: { x: 290, y: 354, w: 120, h: 85 } },
      { id: 'lounge-right', name: 'Open Seating', type: 'utility', bookable: false, commonArea: true, floor: { x: 1514, y: 20, w: 311, h: 178 } },
      { id: 'open-seating', name: 'Open Seating', type: 'utility', bookable: false, commonArea: true, floor: { x: 1839, y: 135, w: 298, h: 205 } },
      { id: 'toilet',       name: 'Unisex Bathroom', type: 'utility', bookable: false, floor: { x: 1983, y: 18, w: 144, h: 105 } },
      { id: 'break-room',   name: 'Kitchen', type: 'utility', bookable: false, commonArea: true, floor: { x: 1571, y: 340, w: 254, h: 165 } },
      { id: 'womens-rr',    name: "Women's RR", type: 'utility', bookable: false, floor: { x: 1880, y: 415, w: 220, h: 140 } },
      { id: 'mens-rr',      name: "Men's RR", type: 'utility', bookable: false, floor: { x: 1880, y: 560, w: 220, h: 135 } },
      { id: 'it-hvac',      name: 'IT Room / HVAC Chase', type: 'utility', bookable: false, rotateLabel: true, floor: { x: 1480, y: 21, w: 28, h: 177 } },
      // Assigned offices (non-bookable)
      {
        id: 'michaels-office',
        name: "Michael's Office",
        type: 'office',
        capacity: 1,
        bookable: false,
        assignedTo: 'M. Measels',
        mapLabel: 'CPO',
        floor: { x: 913, y: 269, w: 175, h: 170 },
      },
      {
        id: 'dustins-office',
        name: "Dustin's Office",
        type: 'office',
        capacity: 1,
        bookable: false,
        assignedTo: 'D. Sutton',
        mapLabel: 'General Manager',
        floor: { x: 1052, y: 21, w: 138, h: 177 },
      },
      // Desks D1-D5: "Shared Office for 5 ppl" (center-left room)
      { id: 'D1',  name: 'Desk D1',  type: 'cubicle', capacity: 1, email: 'hsv.d1@3-gis.com',  floor: { x: 445, y: 269, w: 70, h: 82 } },
      { id: 'D2',  name: 'Desk D2',  type: 'cubicle', capacity: 1, email: 'hsv.d2@3-gis.com',  floor: { x: 521, y: 269, w: 70, h: 82 } },
      { id: 'D3',  name: 'Desk D3',  type: 'cubicle', capacity: 1, email: 'hsv.d3@3-gis.com',  floor: { x: 445, y: 357, w: 70, h: 82 } },
      { id: 'D4',  name: 'Desk D4',  type: 'cubicle', capacity: 1, email: 'hsv.d4@3-gis.com',  floor: { x: 521, y: 357, w: 70, h: 82 } },
      { id: 'D5',  name: 'Desk D5',  type: 'cubicle', capacity: 1, email: 'hsv.d5@3-gis.com',  floor: { x: 597, y: 357, w: 70, h: 82 } },
      // Desks D6-D11: "Shared Office for 6 ppl" (Open Office 311, top right)
      { id: 'D6',  name: 'Desk D6',  type: 'cubicle', capacity: 1, email: 'hsv.d6@3-gis.com',  floor: { x: 1204, y: 21, w: 86, h: 88 } },
      { id: 'D7',  name: 'Desk D7',  type: 'cubicle', capacity: 1, email: 'hsv.d7@3-gis.com',  floor: { x: 1296, y: 21, w: 86, h: 88 } },
      { id: 'D8',  name: 'Desk D8',  type: 'cubicle', capacity: 1, email: 'hsv.d8@3-gis.com',  floor: { x: 1388, y: 21, w: 86, h: 88 } },
      { id: 'D9',  name: 'Desk D9',  type: 'cubicle', capacity: 1, email: 'hsv.d9@3-gis.com',  floor: { x: 1204, y: 115, w: 86, h: 83 } },
      { id: 'D10', name: 'Desk D10', type: 'cubicle', capacity: 1, email: 'hsv.d10@3-gis.com', floor: { x: 1296, y: 115, w: 86, h: 83 } },
      { id: 'D11', name: 'Desk D11', type: 'cubicle', capacity: 1, email: 'hsv.d11@3-gis.com', floor: { x: 1388, y: 115, w: 86, h: 83 } },
      // Desks D12-D19: two "Shared Office" rooms (8-desk cluster, center-right)
      { id: 'D12', name: 'Desk D12', type: 'cubicle', capacity: 1, email: 'hsv.d12@3-gis.com', floor: { x: 1102, y: 269, w: 71, h: 82 } },
      { id: 'D13', name: 'Desk D13', type: 'cubicle', capacity: 1, email: 'hsv.d13@3-gis.com', floor: { x: 1179, y: 269, w: 71, h: 82 } },
      { id: 'D14', name: 'Desk D14', type: 'cubicle', capacity: 1, email: 'hsv.d14@3-gis.com', floor: { x: 1264, y: 269, w: 71, h: 82 } },
      { id: 'D15', name: 'Desk D15', type: 'cubicle', capacity: 1, email: 'hsv.d15@3-gis.com', floor: { x: 1341, y: 269, w: 71, h: 82 } },
      { id: 'D16', name: 'Desk D16', type: 'cubicle', capacity: 1, email: 'hsv.d16@3-gis.com', floor: { x: 1102, y: 357, w: 71, h: 82 } },
      { id: 'D17', name: 'Desk D17', type: 'cubicle', capacity: 1, email: 'hsv.d17@3-gis.com', floor: { x: 1179, y: 357, w: 71, h: 82 } },
      { id: 'D18', name: 'Desk D18', type: 'cubicle', capacity: 1, email: 'hsv.d18@3-gis.com', floor: { x: 1264, y: 357, w: 71, h: 82 } },
      { id: 'D19', name: 'Desk D19', type: 'cubicle', capacity: 1, email: 'hsv.d19@3-gis.com', floor: { x: 1341, y: 357, w: 71, h: 82 } },
    ],

    fixedAreas: [],
  },

  // TEMPLATE: Copy this block and fill in for a new office
  // tampa: {
  //   id: 'tampa',
  //   name: 'Tampa',
  //   fullName: 'SSP Innovations - Tampa Office',
  //   address: 'Tampa, FL',
  //   svgViewBox: '0 0 1200 600',
  //   rooms: [ ... ],
  //   fixedAreas: [ ... ],
  // },
};

// Map subdomains to office IDs so each office's custom domain
// (e.g., hsv.ssp.team, hsv.3-gis.team, mke.ssp.team) resolves correctly.
const SUBDOMAIN_OFFICE_MAP = {
  mke: 'wauwatosa',
  hsv: 'hsv',
};

/**
 * Get office config based on query param, URL path, or subdomain.
 * Resolution order:
 *   1. ?office=hsv
 *   2. /hsv path segment
 *   3. Subdomain (hsv.ssp.team or hsv.3-gis.team -> hsv)
 * Falls back to the first configured office.
 */
export function getOfficeConfig() {
  const params = new URLSearchParams(window.location.search);
  const officeParam = params.get('office');
  const pathSegment = window.location.pathname.split('/').filter(Boolean)[0];
  const subdomain = window.location.hostname.split('.')[0].toLowerCase();
  const subdomainOffice = SUBDOMAIN_OFFICE_MAP[subdomain];

  const officeId = officeParam || pathSegment || subdomainOffice || Object.keys(OFFICE_CONFIGS)[0];
  return OFFICE_CONFIGS[officeId] || OFFICE_CONFIGS[Object.keys(OFFICE_CONFIGS)[0]];
}
